// Polyfill for browsers predating ES2022 (e.g. Samsung Internet < 17, Chrome < 93),
// which still hit the inbox views using Object.hasOwn.
import 'core-js/actual/object/has-own';
import 'core-js/actual/array/at';
import 'core-js/actual/string/at';
import {createApp} from 'vue';
import {createPinia} from 'pinia';
import Maska from 'maska';
import {messaging} from './plugins/firebase.js';
import AppLayout from '@/views/layouts/AppLayout.vue';
import cookieStoragePlugin from "./pinia/cookieStorage";
import App from '@/App.vue';
import router from '@/router';
import '@/assets/style/site.scss';
import Vue3Toastify, {toast} from "vue3-toastify";
import 'vue3-toastify/dist/index.css';
import {useSubdomain} from "@/composables/useSubdomain";
import {useAuth} from "@/stores/AuthStore";
import {CometChat} from '@cometchat/chat-sdk-javascript'
import {useCometChat} from "@/composables/useCometChat";
import * as Sentry from "@sentry/vue";
import {chunkRecoveryFailed, isChunkAssetUrl, isStaleChunkEvent} from "@/utils/staleChunk";
import '@vuepic/vue-datepicker/dist/main.css'


async function loadData() {
    const {getSubdomain} = useSubdomain();
    const {init, isAuthenticated} = useAuth();

    const subdomain = getSubdomain();

    if (!subdomain) {
        if (isAuthenticated) {
            return router.push('launchpad');
        }
        return;
    }

    return init(subdomain);
}

var appSetting = new CometChat.AppSettingsBuilder()
    .subscribePresenceForAllUsers()
    .setRegion(window.appConfig.COMETCHAT_REGION)
    .build();

// Session expiry is fully handled UX: the axios interceptor drops the session
// and redirects to /login, so 401s carry no signal worth reporting. Requests
// that never reached the server (flaky connection, navigation abort, timeout)
// carry no app signal either; fetch-based failures without an app frame are
// the same class reported through window.fetch instrumentation.
function filterSentryEvent(event, hint) {
    const cause = hint?.originalException;

    if (cause?.isAxiosError) {
        if (cause.response?.status === 401) {
            return null;
        }

        if (!cause.response && ['ERR_NETWORK', 'ECONNABORTED', 'ERR_CANCELED', 'ETIMEDOUT'].includes(cause.code)) {
            return null;
        }
    }

    // The CometChat SDK rejects with a plain {error: {code, name, message}}
    // wrapper when its stored auth token is absent. Every SDK failure shares
    // that wrapper shape, so match the noise codes explicitly — send
    // failures, credential or API errors must keep reporting.
    if (cause && typeof cause === 'object' && !(cause instanceof Error)
        && ['USER_NOT_LOGED_IN', 'FAILED_TO_FETCH'].includes(cause.error?.code)) {
        return null;
    }

    // Stale-chunk failures self-heal via the single-reload recovery in the
    // router; reporting them only re-counts every deploy. A chunk failure
    // recurring after the recovery reload means the deploy itself is broken
    // and must keep reporting.
    if (isStaleChunkEvent(event) && !chunkRecoveryFailed()) {
        return null;
    }

    const exception = event.exception?.values?.[0];
    const frames = exception?.stacktrace?.frames ?? [];

    // App frames are /src/ paths in dev and hashed bundle assets in
    // production; injected scripts carry the page URL or an extension origin.
    if (exception?.type === 'TypeError'
        && ['Failed to fetch', 'Load failed', 'NetworkError when attempting to fetch a resource.'].includes(exception.value)
        && !frames.some((frame) => {
            const filename = frame.filename ?? '';

            return filename.includes('/src/') || isChunkAssetUrl(filename);
        })) {
        return null;
    }

    // Scanner/bot environments patch the global Error constructor; every
    // event they produce carries their 'new new_constructor' frame.
    if (frames.some((frame) => frame.function === 'new new_constructor')) {
        return null;
    }

    // Injected scripts (Chrome-on-iOS autofill/translate layers) recursing in
    // their own code: a single URL-less frame and no app filename. A bare
    // ignoreErrors match would also hide genuine app recursion bugs.
    if ((exception?.value ?? '').startsWith('Maximum call stack size exceeded')
        && frames.length <= 1
        && !frames.some((frame) => /connectplus\.app|app:\/\//.test(frame.filename ?? ''))) {
        return null;
    }

    return event;
}

(async () => {
    const pinia = createPinia()
        .use(cookieStoragePlugin);

    const app = createApp(App)
        .use(pinia)
        .use(Maska)
        .use(Vue3Toastify,
            {
                autoClose: 4000,
                icon: false,
            })
        .component('AppLayout', AppLayout)

    const tracePropagationTargets = [
        window.appConfig.GATEWAY_API_URL,
        window.appConfig.COORDINATOR_API_URL,
        window.appConfig.ORG_API_URL,
        window.appConfig.DIRECT_ORG_API_URL,
    ];

    const environmentSampleRates = {
        'staging': 0.00,
        'pre-production': 0.00,
        'production': 0.01,
    };

    Sentry.init({
        app,
        release: window.appConfig.SENTRY_RELEASE,
        environment: window.appConfig.APP_ENV,
        dsn: window.appConfig.SENTRY_DSN,
        integrations: [
            new Sentry.BrowserTracing({
                // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
                tracePropagationTargets,
                routingInstrumentation: Sentry.vueRouterInstrumentation(router),
            }),
            new Sentry.Replay(),
        ],
        tracePropagationTargets,
        // Performance Monitoring
        tracesSampleRate: environmentSampleRates[window.appConfig.APP_ENV] ?? 0.0, // Capture 100% of the transactions, reduce in production!
        // Session Replay
        replaysSessionSampleRate: environmentSampleRates[window.appConfig.APP_ENV] ?? 0.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: environmentSampleRates[window.appConfig.APP_ENV] ?? 0.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        ignoreErrors: [
            /messaging\/(unsupported-browser|indexed-db-unsupported|failed-service-worker-registration)/,
            /Object Not Found Matching Id:\d+, MethodName:[^,]+, ParamCount:\d+/,
            'Invalid call to runtime.sendMessage(). Tab not found.',
            'WKWebView API client did not respond to this postMessage',
            /window\.ethereum/,
            /window\.__firefox__/,
            "Can't find variable: __firefox__",
            'feature named `pageContext` was not found',
            /Blocked a frame with origin .* from accessing a cross-origin frame/,
            'Event `CustomEvent` (type=unhandledrejection) captured as promise rejection',
            'Object captured as promise rejection with keys: [object has no keys]',
        ],
        beforeSend: filterSentryEvent,
    });

    app.provide('messaging', messaging);

    const { init: initCometChat } = useCometChat();

    await Promise.all([
        initCometChat(window.appConfig.COMETCHAT_APP_ID, appSetting).catch((error) => {
            Sentry.captureException(error);
            toast.error('Chat service is temporarily unavailable. Some features may be limited.');
        }),
        loadData().catch((error) => {
            if (error?.response?.status !== 404) {
                Sentry.captureException(error);
                toast.error('Something went wrong while loading your data. Please refresh the page.');
            }
        }),
    ]);

    app.use(router);
    app.mount('#app');
})();


